import { useEffect, useState, useCallback } from "react";
import {
  Box, Sheet, Typography, Button, Table, IconButton, Chip, Modal, ModalDialog,
  FormControl, FormLabel, Input, Select, Option, Alert, Divider, Stack, Switch,
} from "@mui/joy";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import { adminApi } from "../adminApi";
import { useAdminAuth } from "../AdminAuthContext";

const ROLES = [
  { value: "superadmin", label: "Супер-администратор" },
  { value: "admin", label: "Администратор" },
  { value: "viewer", label: "Просмотр" },
];
const roleLabel = (v) => ROLES.find((r) => r.value === v)?.label || v;

export default function StaffPage() {
  const { staff: me } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | {} (create) | row (edit)
  const [pwTarget, setPwTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setRows(await adminApi.listStaff()); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (row) => {
    if (!window.confirm(`Удалить сотрудника ${row.email}?`)) return;
    try { await adminApi.deleteStaff(row.id); load(); }
    catch (e) { alert(e.message); }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography level="h3" sx={{ mr: "auto" }}>Сотрудники</Typography>
        <Button startDecorator={<AddRoundedIcon />} onClick={() => setEditing({})}>Добавить</Button>
      </Box>

      {error && <Alert color="danger" variant="soft">{error}</Alert>}

      <Sheet variant="outlined" sx={{ borderRadius: "12px", overflow: "auto" }}>
        <Table hoverRow>
          <thead>
            <tr>
              <th>Email</th>
              <th>Имя</th>
              <th>Роль</th>
              <th>Статус</th>
              <th style={{ width: 150 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.email}</td>
                <td>{r.full_name || "—"}</td>
                <td><Chip size="sm" variant="soft">{r.role_label || roleLabel(r.role)}</Chip></td>
                <td>
                  {r.is_blocked
                    ? <Chip size="sm" color="danger" variant="soft">Заблокирован</Chip>
                    : <Chip size="sm" color="success" variant="soft">Активен</Chip>}
                </td>
                <td>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="sm" variant="plain" onClick={() => setEditing(r)}><EditRoundedIcon /></IconButton>
                    <IconButton size="sm" variant="plain" onClick={() => setPwTarget(r)}><KeyRoundedIcon /></IconButton>
                    <IconButton size="sm" variant="plain" color="danger" disabled={r.id === me?.id} onClick={() => onDelete(r)}>
                      <DeleteRoundedIcon />
                    </IconButton>
                  </Stack>
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr>
                <td colSpan={5}><Typography level="body-sm" color="neutral" sx={{ p: 1 }}>Нет сотрудников</Typography></td>
              </tr>
            )}
          </tbody>
        </Table>
      </Sheet>

      {editing !== null && (
        <StaffEditModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {pwTarget && (
        <ResetPasswordModal
          target={pwTarget}
          onClose={() => setPwTarget(null)}
          onDone={() => setPwTarget(null)}
        />
      )}
    </Box>
  );
}

function StaffEditModal({ initial, onClose, onSaved }) {
  const isCreate = !initial?.id;
  const [email, setEmail] = useState(initial.email || "");
  const [firstName, setFirstName] = useState(initial.first_name || "");
  const [lastName, setLastName] = useState(initial.last_name || "");
  const [phone, setPhone] = useState(initial.phone || "");
  const [role, setRole] = useState(initial.role || "viewer");
  const [isBlocked, setIsBlocked] = useState(!!initial.is_blocked);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      if (isCreate) {
        await adminApi.createStaff({
          email: email.trim(), password, first_name: firstName,
          last_name: lastName, phone, role,
        });
      } else {
        await adminApi.updateStaff(initial.id, {
          first_name: firstName, last_name: lastName, phone, role, is_blocked: isBlocked,
        });
      }
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose}>
      <ModalDialog sx={{ width: 420, maxWidth: "100%" }}>
        <Typography level="title-lg">{isCreate ? "Новый сотрудник" : "Редактирование"}</Typography>
        {error && <Alert color="danger" variant="soft" sx={{ my: 1 }}>{error}</Alert>}

        <Stack spacing={1.2} sx={{ mt: 1 }}>
          {isCreate && (
            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </FormControl>
          )}
          {isCreate && (
            <FormControl>
              <FormLabel>Пароль</FormLabel>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </FormControl>
          )}
          <FormControl>
            <FormLabel>Имя</FormLabel>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel>Фамилия</FormLabel>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel>Телефон</FormLabel>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel>Роль</FormLabel>
            <Select value={role} onChange={(_e, v) => v && setRole(v)}>
              {ROLES.map((r) => <Option key={r.value} value={r.value}>{r.label}</Option>)}
            </Select>
          </FormControl>
          {!isCreate && (
            <FormControl orientation="horizontal" sx={{ justifyContent: "space-between" }}>
              <FormLabel>Заблокирован</FormLabel>
              <Switch checked={isBlocked} onChange={(e) => setIsBlocked(e.target.checked)} />
            </FormControl>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button variant="plain" color="neutral" onClick={onClose}>Отмена</Button>
          <Button loading={saving} onClick={save} disabled={isCreate && (!email || password.length < 6)}>Сохранить</Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}

function ResetPasswordModal({ target, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      await adminApi.resetStaffPassword(target.id, pw);
      onDone();
      alert("Пароль сброшен. Сотрудник будет разлогинен на всех устройствах.");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose}>
      <ModalDialog sx={{ width: 380, maxWidth: "100%" }}>
        <Typography level="title-lg">Сброс пароля</Typography>
        <Typography level="body-sm" color="neutral">{target.email}</Typography>
        {error && <Alert color="danger" variant="soft" sx={{ my: 1 }}>{error}</Alert>}
        <FormControl sx={{ mt: 1 }}>
          <FormLabel>Новый пароль</FormLabel>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
        </FormControl>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2 }}>
          <Button variant="plain" color="neutral" onClick={onClose}>Отмена</Button>
          <Button loading={saving} onClick={save} disabled={pw.length < 6}>Сбросить</Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}